import type { Rng } from '../rng';
import type { LatLon } from '../geo';
import { distanceNm, isInsideTriangle } from '../geo';
import type { Bathymetry } from '../map/bathymetry';
import type { OutcomeWindows } from './outcome';

/**
 * Notrufe von Schiffen und Flugzeugen.
 *
 * Die Zeitfenster unterscheiden sich stark nach Art des Vorfalls, und genau
 * daraus entstehen die interessanten Entscheidungen: Ein Flugzeugabsturz laesst
 * kaum Zeit fuer eine Rettung, hinterlaesst dafuer aber lange auffindbare
 * Truemmer. Ein Fischerboot treibt tagelang, ist danach aber spurlos weg.
 */

export type IncidentKind =
  | 'cargo_vessel'
  | 'fishing_boat'
  | 'private_yacht'
  | 'airliner'
  | 'light_aircraft'
  | 'naval_patrol';

export interface IncidentType {
  readonly kind: IncidentKind;
  readonly nameKey: string;
  /** Basisfenster bei ruhiger See, in Stunden. */
  readonly windows: OutcomeWindows;
  readonly crewRange: readonly [number, number];
  /** Relative Haeufigkeit. */
  readonly weight: number;
  /** Luftfahrzeuge stuerzen ab, Schiffe treiben — das aendert die Suche. */
  readonly airborne: boolean;
}

/*
 * Zu den Zeitfenstern.
 *
 * Sie waren bis M9 deutlich zu eng. Gemessen: Bei sofortigem Auslaufen und
 * voller Fahrt war eine **Rettung in drei Prozent der Faelle** erreichbar, bei
 * Luftfahrzeugen in null. Der Grund ist Geometrie — ein Notruf liegt im Mittel
 * 441 Seemeilen entfernt, das sind 23 Stunden bei voller Fahrt, und darauf kam
 * ein Rettungsfenster von 22 bis 36 Stunden ab dem *Vorfall*, nicht ab dem
 * Anruf.
 *
 * Damit gab es das Dilemma des Spiels gar nicht: Man entscheidet sich nie
 * gegen eine Rettung, man kommt einfach immer zu spaet. Erst wenn eine
 * Rettung bei zuegigem Auslaufen ungefaehr jedes zweite Mal gelingt, wird das
 * Liegenlassen eines Notrufs zu einer Entscheidung, die man verantworten muss.
 *
 * Luftfahrzeuge bleiben bewusst nahezu hoffnungslos. Das ist kein
 * Ausgleichsfehler, sondern die Sache selbst: Nach einem Aufschlag ins Wasser
 * bleibt selten jemand uebrig. Dafuer halten sich ihre Truemmerfelder am
 * laengsten.
 */
export const INCIDENT_TYPES: readonly IncidentType[] = [
  {
    kind: 'cargo_vessel',
    nameKey: 'incident.cargo_vessel',
    // Grosse Schiffe sinken langsam; Rettungsinseln halten sich lange.
    windows: { rescueHours: 52, salvageHours: 120, coldHours: 260 },
    crewRange: [12, 26],
    weight: 3,
    airborne: false,
  },
  {
    kind: 'fishing_boat',
    nameKey: 'incident.fishing_boat',
    // Kleine Besatzung, treibt lange, hinterlaesst aber fast nichts.
    windows: { rescueHours: 44, salvageHours: 84, coldHours: 150 },
    crewRange: [3, 7],
    weight: 4,
    airborne: false,
  },
  {
    kind: 'private_yacht',
    nameKey: 'incident.private_yacht',
    windows: { rescueHours: 40, salvageHours: 92, coldHours: 180 },
    crewRange: [2, 6],
    weight: 3,
    airborne: false,
  },
  {
    kind: 'airliner',
    nameKey: 'incident.airliner',
    // Kaum Zeit fuer Ueberlebende, dafuer ein weites, langlebiges Truemmerfeld.
    windows: { rescueHours: 16, salvageHours: 150, coldHours: 320 },
    crewRange: [60, 180],
    weight: 1,
    airborne: true,
  },
  {
    kind: 'light_aircraft',
    nameKey: 'incident.light_aircraft',
    windows: { rescueHours: 13, salvageHours: 76, coldHours: 170 },
    crewRange: [1, 4],
    weight: 3,
    airborne: true,
  },
  {
    kind: 'naval_patrol',
    nameKey: 'incident.naval_patrol',
    // Gut ausgeruestet, meldet sich lange — und wird lange gesucht.
    windows: { rescueHours: 58, salvageHours: 132, coldHours: 280 },
    crewRange: [8, 20],
    weight: 1,
    airborne: false,
  },
];

export function incidentType(kind: IncidentKind): IncidentType {
  const found = INCIDENT_TYPES.find((t) => t.kind === kind);
  if (found === undefined) {
    throw new Error(`Unbekannter Vorfalltyp: ${kind}`);
  }
  return found;
}

export interface Incident {
  readonly id: string;
  readonly kind: IncidentKind;
  readonly position: LatLon;
  /** Kampagnenzeit des Vorfalls, in Stunden seit Spielbeginn. */
  readonly occurredAtHours: number;
  readonly souls: number;
  /** Tatsaechliche Fenster inklusive Wetter- und Zufallseinfluss. */
  readonly windows: OutcomeWindows;
  /**
   * Was die Station *glaubt*, wie lange die Fenster reichen.
   *
   * Weicht bewusst von der Wahrheit ab: Niemand weiss vorher, wie lange
   * Menschen in dieser See durchhalten. Alle Anzeigen vor dem Auslaufen
   * rechnen hiermit, die Auswertung am Einsatzort mit `windows`. Deshalb kann
   * eine Einschaetzung danebenliegen — und deshalb ist die Fahrt eine
   * Entscheidung und keine Rechnung.
   */
  readonly estimatedWindows: OutcomeWindows;
  /** Streuung, mit der die Station bei diesem Vorfall rechnet, in Stunden. */
  readonly estimateUncertaintyHours: number;
  /** Name des Fahrzeugs, fuer Funkspruch und Logbuch. */
  readonly vesselName: string;
  /** Tiefe am Fundort in Metern — begrenzt, was das U-Boot erreichen kann. */
  readonly depthMetres: number;
}

/** Wetter verkuerzt vor allem das Rettungsfenster. */
export function applySeaState(windows: OutcomeWindows, seaState: number): OutcomeWindows {
  // Seegang 0..9; bei schwerer See ueberlebt niemand lange im Wasser. Der
  // Boden liegt hoeher als frueher (0,3): Zusammen mit den engen Fenstern von
  // damals war eine Rettung bei Sturm rechnerisch ausgeschlossen, und ein
  // Ausgang, den man nie erreicht, ist keiner.
  const rescueFactor = Math.max(0.55, 1 - seaState * 0.05);
  // Truemmer verteilen sich schneller, halten sich aber grundsaetzlich laenger.
  const salvageFactor = Math.max(0.5, 1 - seaState * 0.04);

  return {
    rescueHours: windows.rescueHours * rescueFactor,
    salvageHours: windows.salvageHours * salvageFactor,
    coldHours: windows.coldHours * salvageFactor,
  };
}

const NAME_PREFIXES = ['MV', 'SS', 'FV', 'MY', 'USCG', 'Flug'] as const;
const NAME_WORDS = [
  'Aurora', 'Callisto', 'Perdido', 'Nightjar', 'Corviser', 'Halcyon', 'Marisol',
  'Absalom', 'Tern', 'Cordelia', 'Vesper', 'Orinoco', 'Sable', 'Kestrel',
  'Thessaly', 'Windward', 'Meridian', 'Calloway', 'Ismene', 'Petrel',
] as const;

function generateName(rng: Rng, airborne: boolean): string {
  if (airborne) {
    return `${rng.pick(['AT', 'CX', 'NV', 'LR'])}-${rng.intInclusive(100, 999)}`;
  }
  return `${rng.pick(NAME_PREFIXES.slice(0, 5))} ${rng.pick(NAME_WORDS)}`;
}

/**
 * Zieht eine Position im Dreieck, die auf offener See liegt.
 *
 * Verwirft Land und sehr flaches Wasser: Ein Notruf auf einer Sandbank waere
 * kein Raetsel, sondern ein Ablesefehler. Nach genuegend Fehlversuchen wird
 * aufgegeben, damit die Erzeugung nie haengen bleibt.
 *
 * ## Warum gleichverteilt und nicht ueber die Tiefen gestreut
 *
 * Ein Versuch, die Fundorte ueber die Tiefen zu streuen, damit auch das
 * Forschungsschiff bergen kann, ist an der Geografie gescheitert: Flaches
 * Wasser gibt es in diesem Ausschnitt fast nur auf den Bahamabaenken — also
 * gleich neben der Station. Die mittlere Entfernung fiel dadurch von 441 auf
 * 278 Seemeilen, und das halbe Spiel wurde nebenbei leichter.
 *
 * Das Dreieck **ist** ein Tiefseebecken, im Median ueber 5.000 m. Daraus folgt
 * eine Rollenteilung, die ehrlicher ist als jede Streuung: Das Schiff ist
 * schnell und rettet Menschen, das U-Boot ist langsam und holt die Beweise
 * herauf. Genau das ist das Dilemma des Spiels, nur in Fahrzeugen ausgedrueckt.
 */
function drawOpenSeaPosition(rng: Rng, bathymetry: Bathymetry): LatLon | null {
  for (let attempt = 0; attempt < 200; attempt++) {
    const candidate: LatLon = {
      lat: rng.float(18.6, 32.0),
      lon: rng.float(-79.5, -65.0),
    };

    if (!isInsideTriangle(candidate)) continue;
    if (bathymetry.depthAt(candidate) < 200) continue;

    return candidate;
  }
  return null;
}

export interface IncidentDraft {
  readonly rng: Rng;
  readonly bathymetry: Bathymetry;
  readonly occurredAtHours: number;
  readonly seaState: number;
  readonly id: string;
}

export function generateIncident(draft: IncidentDraft): Incident | null {
  const { rng, bathymetry, occurredAtHours, seaState, id } = draft;

  const position = drawOpenSeaPosition(rng, bathymetry);
  if (position === null) return null;

  const type = rng.pickWeighted(INCIDENT_TYPES, (t) => t.weight);

  // Streuung, damit zwei Vorfaelle desselben Typs sich nicht gleich anfuehlen.
  const jitter = rng.float(0.85, 1.2);
  const base = applySeaState(type.windows, seaState);

  const windows: OutcomeWindows = {
    rescueHours: base.rescueHours * jitter,
    salvageHours: base.salvageHours * jitter,
    coldHours: base.coldHours * jitter,
  };

  // Die Schaetzung der Station liegt um bis zu einem Fuenftel daneben — mal zu
  // optimistisch, mal zu vorsichtig. Bei schwerer See faellt sie schlechter aus,
  // weil sich der Zustand der Ueberlebenden dann kaum abschaetzen laesst.
  const bias = rng.float(-0.2, 0.2) * (1 + seaState * 0.05);
  const estimatedWindows: OutcomeWindows = {
    rescueHours: Math.max(1, windows.rescueHours * (1 + bias)),
    salvageHours: Math.max(2, windows.salvageHours * (1 + bias * 0.6)),
    coldHours: Math.max(3, windows.coldHours * (1 + bias * 0.4)),
  };

  return {
    id,
    kind: type.kind,
    position,
    occurredAtHours,
    souls: rng.intInclusive(type.crewRange[0], type.crewRange[1]),
    windows,
    estimatedWindows,
    // Wie unsicher sich die Station ist. Waechst mit dem Seegang.
    estimateUncertaintyHours: windows.rescueHours * (0.18 + seaState * 0.02),
    vesselName: generateName(rng, type.airborne),
    depthMetres: bathymetry.depthAt(position),
  };
}

/** Entfernung von einem Ort zum Vorfall, in Seemeilen. */
export function distanceToIncident(from: LatLon, incident: Incident): number {
  return distanceNm(from, incident.position);
}
